import { store } from '@/store';
import { fetchChatInit } from '@/store/api/call';
import {
  setCallStreamInit,
  setCallStreamLoading,
  clearCallStreamLoading,
  mergeChatMessageMetadataFromInit,
  setSessionCaseInfo,
} from '@/store/slices/callSlice';
import { fetchCustomerForCall } from '@/store/slices/customerSlice';
import { clearCaseInfo, fetchCaseById } from '@/store/slices/caseSlice';
import { normalizeChatInitMessages } from '@/utils/chatMessages';
import { resolveContactWorkSpaceID } from '@/utils/contactWorkspace';

const CHAT_SERVICE_DEBUG_PREFIX = '[ChatDebug][chatService]';
const CHAT_DEBUG_STORAGE_KEY = 'spog.chatMessages.debug';

function debugLog(label: string, payload?: unknown): void {
  if (
    typeof window === 'undefined' ||
    window.localStorage.getItem(CHAT_DEBUG_STORAGE_KEY) !== 'true'
  ) {
    return;
  }
  console.log(`${CHAT_SERVICE_DEBUG_PREFIX} ${label}`, payload);
}

function clearCaseForContact(contactId: string): void {
  store.dispatch(setSessionCaseInfo({ contactId, caseInfo: null }));
  if (store.getState().call.focusedContactId === contactId) {
    store.dispatch(clearCaseInfo());
  }
}

/**
 * ChatService: fetches chat init via GET (one-shot JSON) when the agent accepts a chat.
 * History and metadata come from GET /chat/init; live messages via Connect ChatJS.
 */
export const chatService = {
  /**
   * Load chat context for the given contact: init data, customer, case.
   * Dispatches setCallStreamInit (same as voice), fetchCustomerForCall, fetchCaseById/clearCaseInfo.
   */
  async initiateChat(
    contactId: string,
    connectionToken: string
  ): Promise<void> {
    try {
      debugLog('initiateChat start', {
        contactId,
        hasConnectionToken: Boolean(connectionToken),
      });
      store.dispatch(
        setCallStreamLoading({
          contactId,
          connectionToken,
          type: 'chat',
          startedAt: Date.now(),
        })
      );
      const data = await fetchChatInit(contactId, connectionToken);
      debugLog('chat init response', {
        contactId,
        data,
      });

      const isDirectConnect = Boolean(data.isDirectConnect);
      const callerPhoneNumber = data.callerPhoneNumber ?? '';
      const caseId = data.caseId ?? null;
      const clientAlert = data.clientAlert;
      const authenticated = data.authenticated;
      const workSpaceID = resolveContactWorkSpaceID(
        data.workspaceId,
        authenticated
      );
      const intentName = data.intentName ?? '';
      const recordingState = data.recordingState ?? 'enabled';

      const previousMessages = isDirectConnect
        ? []
        : normalizeChatInitMessages(data.previousMessages ?? []);
      debugLog('normalized init messages', {
        contactId,
        isDirectConnect,
        previousMessages,
      });

      store.dispatch(
        setCallStreamInit({
          contactId,
          type: 'chat',
          previousMessages,
          callerPhoneNumber,
          callerName: data.callerName ?? '',
          clientAlert,
          authenticated,
          intentName,
          bufferedTranscripts: [],
          recordingState,
          isDirectConnect,
          caseHistory: data.caseHistory ?? [],
          casePrefill: data.casePrefill ?? null,
        })
      );
      debugLog('setCallStreamInit dispatched', {
        contactId,
        previousMessageCount: previousMessages.length,
        callerName: data.callerName ?? '',
        callerPhoneNumber,
      });

      if (workSpaceID) {
        store.dispatch(
          fetchCustomerForCall({
            workSpaceID,
            contactId,
          })
        );
      }

      if (caseId) {
        store.dispatch(fetchCaseById({ caseId, contactId }));
      } else {
        clearCaseForContact(contactId);
      }
    } catch (err) {
      store.dispatch(clearCallStreamLoading({ contactId }));
      console.error('[chatService] Chat init error:', err);
    }
  },

  /** Re-fetch GET /chat/init and merge messageId / notes (e.g. after chat note 404). */
  async refreshChatMessageMetadata(contactId: string): Promise<void> {
    try {
      const connectionToken =
        store.getState().call.sessions[contactId]?.chatConnectionToken ??
        store.getState().call.chatConnectionToken ??
        undefined;
      debugLog('refresh metadata start', {
        contactId,
        hasConnectionToken: Boolean(connectionToken),
      });
      if (!connectionToken) return;
      const data = await fetchChatInit(contactId, connectionToken);
      debugLog('refresh metadata response', {
        contactId,
        previousMessages: data.previousMessages ?? [],
      });
      store.dispatch(
        mergeChatMessageMetadataFromInit({
          contactId,
          messages: data.previousMessages ?? [],
        })
      );
    } catch {
      /* non-fatal */
    }
  },
};
